import { Component } from '@theme/component';

/** @type {Promise<void> | null} */
let googleMapsPromise = null;

const PIN_ICON =
  '<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linejoin="round" d="M10 18s6-6.7 6-11a6 6 0 1 0-12 0c0 4.3 6 11 6 11Z"/><circle cx="10" cy="7" r="2.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)"/></svg>';

/** @param {string} apiKey */
function loadGoogleMaps(apiKey) {
  if (window.google?.maps) return Promise.resolve();
  if (googleMapsPromise) return googleMapsPromise;

  googleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-google-maps-loader]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps')));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    script.async = true;
    script.dataset.googleMapsLoader = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

/**
 * Great-circle distance between two lat/lng points, in kilometers.
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 */
function distanceKm(lat1, lng1, lat2, lng2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

class FindDealerMap extends Component {
  /** @type {any[]} */
  dealers = [];
  /** @type {any[]} */
  filtered = [];
  /** @type {any} */
  map = null;
  /** @type {any} */
  infoWindow = null;
  /** @type {Map<string, any>} */
  markers = new Map();
  /** @type {{lat: number, lng: number} | null} */
  origin = null;

  connectedCallback() {
    super.connectedCallback();

    const dataEl = this.querySelector('[data-dealer-data]');
    try {
      this.dealers = JSON.parse(dataEl?.textContent || '[]').filter((d) => d.lat && d.lng);
    } catch {
      this.dealers = [];
    }
    this.filtered = this.dealers;
    this.contactLabel = this.dataset.contactLabel || 'Contact';

    this.populateModelFilters();
    this.bindFilters();
    this.bindSearch();
    this.renderList();

    const apiKey = this.dataset.googleMapsKey;
    if (!apiKey) {
      console.warn('[find-dealer] No Google Maps API key set on this section.');
      return;
    }

    loadGoogleMaps(apiKey)
      .then(() => this.initMap())
      .catch((error) => console.warn('[find-dealer]', error));
  }

  initMap() {
    const mapEl = this.querySelector('[data-dealer-map]');
    if (!mapEl || !window.google?.maps) return;

    this.map = new window.google.maps.Map(mapEl, {
      center: { lat: 23.7, lng: 121.0 },
      zoom: 7,
      scrollwheel: false,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
    });
    this.infoWindow = new window.google.maps.InfoWindow();

    this.renderMarkers();
    this.fitAllDealers();
  }

  /** Frames every dealer on first load (dealers span Taiwan and the US, across the Pacific). */
  fitAllDealers() {
    const points = this.dealers.filter((d) => isFinite(parseFloat(d.lat)) && isFinite(parseFloat(d.lng)));
    if (points.length < 2) return;
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((d) => bounds.extend({ lat: parseFloat(d.lat), lng: parseFloat(d.lng) }));
    this.map.fitBounds(bounds, 48);
  }

  populateModelFilters() {
    const models = [...new Set(this.dealers.map((d) => d.model).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b)
    );

    const container = this.querySelector('[data-dealer-model-filters]');
    if (!container) return;

    container.innerHTML = models
      .map(
        (model) => `
          <label class="dealer-finder__model">
            <input type="checkbox" data-dealer-model-filter value="${model}">
            <span>${model}</span>
          </label>
        `
      )
      .join('');
  }

  bindFilters() {
    this.querySelectorAll('[data-dealer-model-filter]').forEach((input) => {
      input.addEventListener('change', () => this.applyFilters());
    });
  }

  bindSearch() {
    this.querySelector('[data-dealer-address-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = this.querySelector('[data-dealer-address-input]');
      const address = input?.value.trim();
      if (address) this.geocodeAndCenter(address);
    });

    this.querySelector('[data-dealer-locate]')?.addEventListener('click', () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (position) => this.locateNearest(position.coords.latitude, position.coords.longitude),
        (error) => console.warn('[find-dealer]', error)
      );
    });
  }

  /**
   * "Use my location" only ever needs the single closest dealer, not the
   * whole list re-sorted by distance.
   * @param {number} lat
   * @param {number} lng
   */
  locateNearest(lat, lng) {
    this.origin = { lat, lng };

    const candidates = this.getModelFilteredDealers();
    if (!candidates.length) return;

    const nearest = [...candidates].sort(
      (a, b) =>
        distanceKm(lat, lng, Number(a.lat), Number(a.lng)) - distanceKm(lat, lng, Number(b.lat), Number(b.lng))
    )[0];

    this.filtered = [nearest];
    this.renderMarkers();
    this.renderList();
    this.selectDealer(nearest.id);
  }

  /** @param {string} address */
  geocodeAndCenter(address) {
    if (!window.google?.maps) return;

    new window.google.maps.Geocoder().geocode({ address }, (results, status) => {
      if (status !== 'OK' || !results?.[0]) {
        console.warn('[find-dealer] Geocode failed:', status);
        return;
      }
      const location = results[0].geometry.location;
      this.centerOn(location.lat(), location.lng());
    });
  }

  /**
   * @param {number} lat
   * @param {number} lng
   */
  centerOn(lat, lng) {
    this.origin = { lat, lng };

    if (this.map) {
      this.map.panTo({ lat, lng });
      this.map.setZoom(12);
    }

    this.sortByDistance();
    this.renderList();
  }

  sortByDistance() {
    if (!this.origin) return;
    const { lat, lng } = this.origin;
    this.filtered = [...this.filtered].sort(
      (a, b) =>
        distanceKm(lat, lng, Number(a.lat), Number(a.lng)) - distanceKm(lat, lng, Number(b.lat), Number(b.lng))
    );
  }

  getModelFilteredDealers() {
    const checkedModels = [...this.querySelectorAll('[data-dealer-model-filter]:checked')].map(
      (el) => el.value
    );

    return this.dealers.filter((d) => {
      if (checkedModels.length && !checkedModels.includes(d.model)) return false;
      return true;
    });
  }

  applyFilters() {
    this.filtered = this.getModelFilteredDealers();

    this.sortByDistance();
    this.renderMarkers();
    this.renderList();
  }

  renderMarkers() {
    if (!this.map || !window.google?.maps) return;

    this.markers.forEach((marker) => marker.setMap(null));
    this.markers.clear();

    this.filtered.forEach((dealer) => {
      const marker = new window.google.maps.Marker({
        position: { lat: Number(dealer.lat), lng: Number(dealer.lng) },
        map: this.map,
      });

      marker.addListener('click', () => this.selectDealer(dealer.id));
      this.markers.set(String(dealer.id), marker);
    });
  }

  renderList() {
    const countEl = this.querySelector('[data-dealer-count]');
    if (countEl) countEl.textContent = String(this.filtered.length);

    const listEl = this.querySelector('[data-dealer-list]');
    if (!listEl) return;

    listEl.innerHTML = this.filtered
      .map((dealer) => {
        const addressLine2 = [dealer.city, dealer.country].filter(Boolean).join(', ');
        const address = [dealer.address, addressLine2].filter(Boolean).join('\n');

        return `
          <li class="dealer-list__item" data-dealer-item="${dealer.id}">
            <button type="button" class="dealer-list__row" data-dealer-id="${dealer.id}">
              <span class="dealer-list__pin" aria-hidden="true">${PIN_ICON}</span>
              <span class="dealer-list__info">
                <span class="dealer-list__name">${dealer.name || ''}</span>
                ${address ? `<span class="dealer-list__address">${address}</span>` : ''}
                ${dealer.model ? `<span class="dealer-list__model">${dealer.model}</span>` : ''}
              </span>
            </button>
            ${
              dealer.contactLink
                ? `<a class="dealer-list__contact" href="${dealer.contactLink}">${this.contactLabel}</a>`
                : ''
            }
          </li>
        `;
      })
      .join('');

    listEl.querySelectorAll('[data-dealer-id]').forEach((button) => {
      button.addEventListener('click', () => this.selectDealer(button.dataset.dealerId));
    });
  }

  /** @param {string} id */
  selectDealer(id) {
    const dealer = this.dealers.find((d) => String(d.id) === String(id));
    if (!dealer) return;

    if (this.map) {
      this.map.panTo({ lat: Number(dealer.lat), lng: Number(dealer.lng) });
      this.map.setZoom(13);
    }

    this.querySelectorAll('.dealer-list__item').forEach((item) => {
      item.classList.toggle('is-active', item.dataset.dealerItem === String(id));
    });

    this.showInfoWindow(dealer);
  }

  /** @param {any} dealer */
  showInfoWindow(dealer) {
    if (!this.infoWindow || !window.google?.maps) return;

    const marker = this.markers.get(String(dealer.id));
    if (!marker) return;

    const addressLine2 = [dealer.city, dealer.country].filter(Boolean).join(', ');

    this.infoWindow.setContent(`
      <div class="dealer-infowindow">
        <h3 class="dealer-infowindow__name">${dealer.name || ''}</h3>
        ${dealer.address ? `<p class="dealer-infowindow__row">${dealer.address}</p>` : ''}
        ${addressLine2 ? `<p class="dealer-infowindow__row">${addressLine2}</p>` : ''}
        ${dealer.model ? `<p class="dealer-infowindow__model">${dealer.model}</p>` : ''}
        ${
          dealer.contactLink
            ? `<a class="dealer-infowindow__contact" href="${dealer.contactLink}">${this.contactLabel}</a>`
            : ''
        }
      </div>
    `);
    this.infoWindow.open({ map: this.map, anchor: marker });
  }
}

if (!customElements.get('find-dealer-map')) {
  customElements.define('find-dealer-map', FindDealerMap);
}
